import { DataQualityGrade } from '@lune-climate/lune'
import { DataQualityAggregatedTimeseries, DataQualityScoreBreakdown } from '@lune-fe/dapi-models'
import {
    formatNumbers,
    getDataQualityScoreColor,
    getDataQualityScoreFormattedLetter,
} from '@lune-fe/lune-components-lib'
import { LoadingWrapper, Text, Tooltip } from '@lune-fe/lune-ui-lib'
import { Box } from '@mui/material'
import { FC } from 'react'

import ChartEmptyState from 'views/DataHealth/ChartEmptyState'
import { formattedLabels, getPercentage, icons } from 'views/DataHealth/shared'

const sharedTableStyle = {
    display: 'flex',
    gap: 3,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
    '> .MuiTypography-root, > div': {
        lineHeight: '64px',
        '&:nth-child(1)': {
            width: '30%',
        },
        '&:nth-child(2)': {
            width: '15%',
            textAlign: 'right',
        },
        '&:nth-child(3)': {
            width: '5%',
        },
        '&:nth-child(4)': {
            width: '50%',
        },
    },
    '> .MuiTypography-root': {
        lineHeight: '72px',
    },
}

const Bar: FC<{ data: DataQualityScoreBreakdown; totalNumberOfLegs: number }> = ({
    data,
    totalNumberOfLegs,
}) => {
    const totalAndPercentages = getPercentage(totalNumberOfLegs, data.legs)

    return (
        <Box
            sx={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
            }}
        >
            <Tooltip
                style={{ width: `${totalAndPercentages}%`, padding: '0 16px' }}
                title={
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {data.breakdown.a > 0 && (
                            <Text variant={'caption'}>A Excellent: {data.breakdown.a} legs</Text>
                        )}
                        {data.breakdown.b > 0 && (
                            <Text variant={'caption'}>B Good: {data.breakdown.b} legs</Text>
                        )}
                        {data.breakdown.c > 0 && (
                            <Text variant={'caption'}>C Fair: {data.breakdown.c} legs</Text>
                        )}
                        {data.breakdown.d > 0 && (
                            <Text variant={'caption'}>D Poor: {data.breakdown.d} legs</Text>
                        )}
                    </Box>
                }
                placement="top-end"
            >
                <Box
                    sx={{
                        height: '12px',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        sx={{
                            width: `${data.breakdown.d}%`,
                            backgroundColor: getDataQualityScoreColor(DataQualityGrade.D),
                            height: '100%',
                        }}
                    />
                    <Box
                        sx={{
                            width: `${data.breakdown.c}%`,
                            backgroundColor: getDataQualityScoreColor(DataQualityGrade.C),
                            height: '100%',
                        }}
                    />
                    <Box
                        sx={{
                            width: `${data.breakdown.b}%`,
                            backgroundColor: getDataQualityScoreColor(DataQualityGrade.B),
                            height: '100%',
                        }}
                    />
                    <Box
                        sx={{
                            width: `${data.breakdown.a}%`,
                            backgroundColor: getDataQualityScoreColor(DataQualityGrade.A),
                            height: '100%',
                        }}
                    />
                </Box>
            </Tooltip>
            <Text variant={'body3'} sx={{ color: `Grey600` }}>
                {totalAndPercentages > 0.01
                    ? totalAndPercentages.toFixed(2)
                    : totalAndPercentages.toFixed(4)}
                %
            </Text>
        </Box>
    )
}

const DataHealthByModeTable: FC<{
    data: DataQualityAggregatedTimeseries['byMode']
    totalNumberOfLegs: number
    loading: boolean
}> = ({ data, loading, totalNumberOfLegs }) => {
    const localData: (DataQualityScoreBreakdown & { label: string })[] = Object.entries(data)
        .filter((entry): entry is [string, DataQualityScoreBreakdown] => {
            const [, value] = entry
            return !!value?.legs && value.legs > 0
        })
        .map(([label, data]) => ({
            label,
            ...data,
        }))
        .sort((a, b) => b.legs - a.legs)

    return (
        <Box sx={{ mt: 4 }}>
            {localData.length ? (
                <LoadingWrapper loading={loading} sx={{ height: '400px', width: '100%' }}>
                    <Box
                        sx={{
                            minWidth: '800px',
                            width: '100%',
                        }}
                    >
                        <Box sx={sharedTableStyle}>
                            <Text variant={'button'}>Mode</Text>
                            <Text variant={'button'}>Legs</Text>
                            <Box />
                            <Text variant={'button'}>Data Quality</Text>
                        </Box>
                        {localData.map((d, index) => (
                            <Box key={`${d.label}-${index}`} sx={sharedTableStyle}>
                                <Text
                                    variant={'body2'}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-start',
                                        gap: 3,
                                    }}
                                >
                                    {icons[d.label]} {formattedLabels[d.label]}
                                </Text>
                                <Text variant={'body3'}>{formatNumbers(d.legs)}</Text>
                                <Box />
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-start',
                                    }}
                                >
                                    <Text
                                        variant={'body2'}
                                        sx={{
                                            fontWeight: 700,
                                            width: '24px',
                                        }}
                                    >
                                        {getDataQualityScoreFormattedLetter(
                                            d.score as DataQualityGrade,
                                        )}
                                    </Text>
                                    <Bar data={d} totalNumberOfLegs={totalNumberOfLegs} />
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </LoadingWrapper>
            ) : (
                <ChartEmptyState />
            )}
        </Box>
    )
}

export default DataHealthByModeTable
